import React, { useState, useCallback, useMemo, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getExpandedMenuState } from './routeCalculation';
import MenuChild from './MenuChild';
import { Menu as MenuProps } from '../../../../interfaces/common';
import { AuthContext } from '../../../../contexts/AuthContext';
import { hasResourseAccess } from '../../../../utils/methods';

const Menu: React.FC<MenuProps> = ({ id, icon, text, childrens, redirectsTo, sidebarExpanded }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { permissions, isSystemAdmin } = useContext(AuthContext);

  const currentRoute = location.pathname;
  const isSelected = getExpandedMenuState(id, currentRoute);

  const filteredChildrens = useMemo(() => {
    return (childrens || []).filter((tempChild) => {
      return hasResourseAccess(
        isSystemAdmin,
        permissions,
        tempChild.accessibleTo ? tempChild : null
      );
    });
  }, [childrens, permissions, isSystemAdmin]);

  const hasChildren = filteredChildrens && filteredChildrens.length > 0;

  const isChildActive = useMemo(() => {
    if (!hasChildren) return false;
    return filteredChildrens.some((child) => child.redirectsTo && currentRoute.includes(child.redirectsTo));
  }, [hasChildren, filteredChildrens, currentRoute]);

  const isActive = isSelected || isChildActive || (redirectsTo ? currentRoute.startsWith(redirectsTo) : false);

  const [expanded, setExpanded] = useState<boolean>(isSelected || isChildActive);

  const handleItemClick = useCallback(() => {
    if (!sidebarExpanded) {
      if (redirectsTo) {
        navigate(redirectsTo);
      } else if (hasChildren && filteredChildrens[0]?.redirectsTo) {
        navigate(filteredChildrens[0].redirectsTo);
      }
    } else {
      if (hasChildren) {
        setExpanded((prev) => !prev);
      } else if (redirectsTo) {
        navigate(redirectsTo);
      }
    }
  }, [sidebarExpanded, hasChildren, filteredChildrens, redirectsTo, navigate]);

  return (
    <div className="w-100 position-relative">
      <button
        type="button"
        className={`obs-nav-item ${isActive ? 'active' : ''}`}
        onClick={handleItemClick}
      >
        {isActive && <span className="obs-nav-active-bar" />}
        <span className="obs-nav-icon">{icon}</span>
        {sidebarExpanded && <span className="obs-nav-label">{text}</span>}
        {!sidebarExpanded && <span className="obs-flyout">{text}</span>}
        {sidebarExpanded && hasChildren && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className={`obs-nav-arrow ${expanded ? 'open' : ''}`}
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        )}
      </button>
      {hasChildren && sidebarExpanded && (
        <div className={`obs-nav-sub ${expanded ? 'open' : 'closed'}`}>
          {filteredChildrens.map((child) => (
            <MenuChild key={child.text} {...child} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Menu;
